// Console panel driver: renders the log buffer into #console-output and
// wires #console-input → command dispatch. The panel itself (drag/resize,
// show/hide) is handled in events.ts + topbar/index.ts; this module owns
// only the contents.
//
// The buffer + input history live in module state (logger.ts and below),
// so closing and re-opening the panel preserves both within a session.

import type { Store } from '@store/store';
import type { State } from '@store/schema';
import type { ViewerContext } from '@app/context';
import type { WindowRegistry, WindowHandle } from '../windowManager';
import {
    getBuffer, onLog,
    installConsoleCapture, installIntentTaps,
    type LogEntry,
} from './logger';
import { registerBuiltins, runLine } from './commands';

const HISTORY_CAP = 50;
const inputHistory: string[] = [];
let historyCursor = -1;

function formatTime(ms: number): string {
    const d = new Date(ms);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
}

function renderEntry(out: HTMLElement, e: LogEntry): void {
    const line = document.createElement('div');
    line.className = `console-line lvl-${e.level}`;
    const time = document.createElement('span');
    time.className = 'console-time';
    time.textContent = formatTime(e.t);
    line.appendChild(time);
    line.appendChild(document.createTextNode(e.msg));
    out.appendChild(line);
}

function nearBottom(out: HTMLElement): boolean {
    return out.scrollHeight - out.scrollTop - out.clientHeight < 8;
}

export function initConsole(store: Store<State>, viewer: ViewerContext, registry: WindowRegistry): () => void {
    const out = document.getElementById('console-output');
    const form = document.getElementById('console-input-row') as HTMLFormElement | null;
    const input = document.getElementById('console-input') as HTMLInputElement | null;
    const panel = document.getElementById('console-panel');
    if (!out || !form || !input) return () => { /* nothing to wire */ };

    // Register the panel with the window registry — owns drag/resize/close,
    // persists position + size to localStorage across reloads.
    let consoleWindow: WindowHandle | null = null;
    if (panel) {
        const header = panel.querySelector('.console-header') as HTMLElement | null;
        const resize = panel.querySelector('.console-resize') as HTMLElement | null;
        const closeBtn = document.getElementById('console-close');
        if (header) {
            consoleWindow = registry.register({
                id: 'console',
                panel,
                header,
                resize,
                closeBtn,
                minWidth: 360,
                minHeight: 200,
            });
        }
    }

    const offCapture = installConsoleCapture();
    const offTaps = installIntentTaps(store);
    registerBuiltins(viewer, store);

    // Initial paint from whatever pre-init log lines already accumulated
    // (errors during bootstrap, intent taps for the initial dataset, etc.).
    out.textContent = '';
    const initial = getBuffer();
    if (initial.length === 0) {
        // Friendly empty-state hint — not added to the buffer so `clear`
        // wipes it cleanly.
        const hint = document.createElement('div');
        hint.className = 'console-line lvl-debug';
        hint.textContent = "Type 'help' for available commands.";
        out.appendChild(hint);
    } else {
        for (const e of initial) renderEntry(out, e);
        out.scrollTop = out.scrollHeight;
    }

    const offLog = onLog((e) => {
        if (e.level === 'debug' && e.msg === '[cleared]') {
            out.textContent = '';
            return;
        }
        const wasAtBottom = nearBottom(out);
        renderEntry(out, e);
        if (wasAtBottom) out.scrollTop = out.scrollHeight;
    });

    const onSubmit = (ev: SubmitEvent): void => {
        ev.preventDefault();
        const raw = input.value;
        input.value = '';
        const trimmed = raw.trim();
        if (!trimmed) return;
        if (inputHistory[inputHistory.length - 1] !== trimmed) {
            inputHistory.push(trimmed);
            if (inputHistory.length > HISTORY_CAP) inputHistory.splice(0, inputHistory.length - HISTORY_CAP);
        }
        historyCursor = -1;
        void runLine(trimmed);
    };

    const onKeyDown = (ev: KeyboardEvent): void => {
        if (ev.key === 'ArrowUp') {
            if (!inputHistory.length) return;
            ev.preventDefault();
            if (historyCursor === -1) historyCursor = inputHistory.length - 1;
            else historyCursor = Math.max(0, historyCursor - 1);
            input.value = inputHistory[historyCursor] ?? '';
            // Move caret to end on next tick.
            requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
        } else if (ev.key === 'ArrowDown') {
            if (historyCursor === -1) return;
            ev.preventDefault();
            historyCursor += 1;
            if (historyCursor >= inputHistory.length) {
                historyCursor = -1;
                input.value = '';
            } else {
                input.value = inputHistory[historyCursor] ?? '';
            }
        }
    };

    // Focus the input whenever the panel becomes visible — small UX win so
    // the user can start typing immediately after clicking the topbar icon.
    let openObserver: MutationObserver | null = null;
    if (panel) {
        openObserver = new MutationObserver(() => {
            if (panel.classList.contains('show')) {
                requestAnimationFrame(() => input.focus());
                out.scrollTop = out.scrollHeight;
            }
        });
        openObserver.observe(panel, { attributes: true, attributeFilter: ['class'] });
        if (panel.classList.contains('show')) requestAnimationFrame(() => input.focus());
    }

    form.addEventListener('submit', onSubmit);
    input.addEventListener('keydown', onKeyDown);

    return () => {
        form.removeEventListener('submit', onSubmit);
        input.removeEventListener('keydown', onKeyDown);
        if (openObserver) openObserver.disconnect();
        if (consoleWindow) consoleWindow.dispose();
        offLog();
        offTaps();
        offCapture();
    };
}
